/**
 * usePersona — tenant persona flags (Forwarder TMS Wave 1.2).
 *
 * Exposes the tenant's persona as a camelCase object for gating UI/behaviour
 * across modules (shipments, accounting, customs, settlements). Reads the
 * server-cached `/api/v1/tenant/persona` endpoint, available to any
 * authenticated user (not admin-only). The admin toggle on the Company Profile
 * settings page flips the flag and invalidates this query.
 */
import { useQuery } from '@tanstack/react-query';
import { tenantSettingsApi } from '../services/tenantSettingsApi';
import { useAuth } from '../auth';

export const PERSONA_QUERY_KEY = ['tenant-persona'];

export interface Persona {
  sellsFreightServices: boolean;
}

export function usePersona() {
  const { user } = useAuth();

  return useQuery<Persona>({
    queryKey: PERSONA_QUERY_KEY,
    queryFn: async () => {
      const data = await tenantSettingsApi.getPersona();
      return { sellsFreightServices: data.sells_freight_services };
    },
    enabled: !!user?.tenant_id,
    // Persona changes rarely; keep it fresh for a few minutes to match the
    // server-side L2 cache and avoid refetch churn on every mount.
    staleTime: 5 * 60 * 1000,
  });
}
